/**
 * PhotoSlot Component
 *
 * Individual drop target within the layout preview that can receive dragged photos.
 * Displays placed photo or empty placeholder and provides visual feedback during drag operations.
 *
 * Requirements: 3.3, 3.4, 5.3, 5.4, 7.2
 */

import { useState } from 'react';
import { useDrop } from 'react-dnd';
import { DRAG_TYPES, type DraggedPhoto, type PhotoDropResult } from '../types/drag-drop';
import type { LocalFrame } from '../../../lib/db/schema';
import type { EditPhotoMinimal } from '../types/edit-session';

export interface PhotoSlotProps {
  /** Index of this slot in the layout (0-based) */
  slotIndex: number;
  /** ID of the photo currently placed in this slot, or null if empty */
  photoId: string | null;
  /** Photo object if one is placed in this slot */
  photo: EditPhotoMinimal | null;
  /** Selected frame layout (for styling/dimensions) */
  frame: LocalFrame | null;
  /** Callback when a photo is dropped into this slot */
  onPhotoDrop: (photoId: string) => void;
  /** Optional callback when drop operation completes with success status */
  onDropComplete?: (slotIndex: number, photoId: string, replaced: boolean) => void;
}

/**
 * PhotoSlot component - Drop target for photos in the layout preview
 */
export function PhotoSlot({
  slotIndex,
  photoId,
  photo,
  frame: _frame,
  onPhotoDrop,
  onDropComplete,
}: PhotoSlotProps) {
  const [justDropped, setJustDropped] = useState(false);

  /**
   * handleDrop - Called when a photo is dropped into this slot
   * Requirements 5.4, 5.6: Place photo in slot and handle replacement of existing photos
   */
  const handleDrop = (item: DraggedPhoto): PhotoDropResult => {
    const wasOccupied = photoId !== null;
    const replacedPhotoId = photoId;

    console.log(`[PhotoSlot] Photo ${item.photoId} dropped into slot ${slotIndex}`, {
      wasOccupied,
      replacedPhotoId,
    });

    // Call the parent handler to update state
    onPhotoDrop(item.photoId);

    // Show visual confirmation (Requirement 7.4)
    setJustDropped(true);
    setTimeout(() => setJustDropped(false), 500);

    // Notify completion with replacement status
    onDropComplete?.(slotIndex, item.photoId, wasOccupied);

    return { slotIndex, success: true };
  };

  // Set up drop target using react-dnd
  const [{ isOver, canDrop }, drop] = useDrop<
    DraggedPhoto,
    PhotoDropResult,
    { isOver: boolean; canDrop: boolean }
  >(() => ({
    accept: DRAG_TYPES.PHOTO,
    drop: handleDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [slotIndex, photoId, onPhotoDrop, onDropComplete]);

  // Determine visual state
  const isActive = isOver && canDrop;
  const isEmpty = !photo;

  // Create object URL for photo display
  const photoUrl = photo ? URL.createObjectURL(photo.imageBlob) : null;

  return (
    <div
      ref={drop}
      role="button"
      tabIndex={0}
      aria-label={`Photo slot ${slotIndex + 1}${isEmpty ? ', empty' : ', filled'}${isActive ? ', drop target active' : ''}`}
      aria-dropeffect={canDrop ? 'move' : 'none'}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          // Keyboard users can interact with slots
          console.log(`[PhotoSlot] Keyboard interaction for slot ${slotIndex}`);
        }
      }}
      className={`
        relative
        w-full
        aspect-[3/4]
        rounded-lg
        overflow-hidden
        transition-all
        duration-300
        ease-in-out
        focus:outline-none
        focus:ring-4
        focus:ring-yellow-500
        focus:ring-offset-2
        ${isEmpty ? 'border-4 border-dashed' : 'border-4 border-solid'}
        ${isActive ? 'border-yellow-400 bg-yellow-50 scale-105 shadow-xl' : ''}
        ${!isActive && canDrop && isOver === false ? 'border-gray-300' : ''}
        ${!isActive && !isEmpty ? 'border-gray-400 shadow-md' : ''}
        ${!isActive && isEmpty ? 'border-gray-300 shadow-sm' : ''}
        ${justDropped ? 'ring-4 ring-green-400 animate-bounce' : ''}
      `}
      data-testid={`photo-slot-${slotIndex}`}
      data-slot-index={slotIndex}
      data-is-empty={isEmpty}
      data-is-active={isActive}
    >
      {photo && photoUrl ? (
        // Display placed photo
        <img
          src={photoUrl}
          alt={`Photo in slot ${slotIndex + 1}`}
          className="w-full h-full object-cover"
          data-testid={`photo-slot-image-${slotIndex}`}
        />
      ) : (
        // Display empty placeholder
        <div
          className="flex flex-col items-center justify-center h-full text-gray-400 p-4"
          data-testid={`photo-slot-placeholder-${slotIndex}`}
        >
          <svg
            className="w-12 h-12 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span className="text-sm text-center">
            {isActive ? 'Drop photo here' : `Slot ${slotIndex + 1}`}
          </span>
        </div>
      )}

      {/* Visual indicator when drag is over */}
      {isActive && (
        <div className="absolute inset-0 bg-yellow-400 bg-opacity-20 pointer-events-none animate-pulse"
          aria-hidden="true" />
      )}

      {/* Success confirmation overlay (Requirement 7.4) */}
      {justDropped && (
        <div className="absolute inset-0 bg-green-400 bg-opacity-30 pointer-events-none flex items-center justify-center z-10">
          <div className="animate-bounce">
            <svg
              className="w-16 h-16 text-white drop-shadow-lg"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}
